import type { Product } from "../product-step/types";

/**
 * The compare-packages table, entirely from `usp`
 * (`use_case = 'package_comparison'`).
 *
 * Nothing about the catalog is in the code: not which packages have columns,
 * not which rows exist, not their order, not the section they sit in. Adding a
 * package, adding a row or reordering them is a data change plus a translation key.
 *
 * Each package lists only what it INCLUDES. The table is the union of those
 * lists, so absence is what a comparison table means by absence, and a row no
 * package includes simply does not exist.
 */

/** One package's payload, as stored in `usp.usp`. */
export interface ComparisonPayload {
  sections?: {
    id: string;
    /** Icon name, resolved by the component. Same convention as the other
     *  `usp` payloads, which store `"icon": "tube"` and friends. */
    icon?: string;
    /** Long details (the biomarker list is a paragraph) go behind an
     *  affordance rather than printing inline. */
    detail?: "inline" | "toggle";
    /** Row slugs this package includes, in display order. Each resolves to
     *  `mriComparison.row.<slug>` and `…<slug>Info`. */
    rows?: string[];
  }[];
}

export interface ComparisonColumn {
  product: Product;
  payload: ComparisonPayload;
  /** Every slug this package includes, across sections. */
  included: Set<string>;
}

export interface ComparisonSection {
  id: string;
  icon?: string;
  detail?: "inline" | "toggle";
  rows: string[];
}

export function comparisonColumns(
  scanProducts: Product[],
  payloadByProduct: Map<string, ComparisonPayload>,
): ComparisonColumn[] {
  return scanProducts
    .flatMap((product) => {
      const payload = payloadByProduct.get(product.id);
      if (!payload) return [];
      const included = new Set(
        (payload.sections ?? []).flatMap((section) => section.rows ?? []),
      );
      return [{ product, payload, included }];
    })
    .sort((a, b) => (a.product.sort_order ?? 0) - (b.product.sort_order ?? 0));
}

/**
 * The table's rows, merged across the columns.
 *
 * Stable union in column order: a section or row keeps the position the first
 * package to include it gave it, and later packages append what they add. With
 * nested packages (each tier a superset of the one before) that reproduces the
 * intended order exactly; with overlapping ones it is still deterministic,
 * which is the property that matters.
 */
export function comparisonSections(
  columns: ComparisonColumn[],
): ComparisonSection[] {
  const merged: ComparisonSection[] = [];

  for (const column of columns) {
    for (const section of column.payload.sections ?? []) {
      let target = merged.find((s) => s.id === section.id);
      if (!target) {
        target = {
          id: section.id,
          icon: section.icon,
          detail: section.detail,
          rows: [],
        };
        merged.push(target);
      }
      for (const row of section.rows ?? []) {
        if (!target.rows.includes(row)) target.rows.push(row);
      }
    }
  }

  return merged;
}
